/**
 * Interview plan of a job offer (`interviewPlan` of the offer). Its entry `i` describes round `i` of the
 * processes linked to the offer (`recommendedBy.offerId`): the interviewers with their evaluation, the
 * interviews the candidate books, and the email sent when the candidate reaches the round.
 *
 * The plan is read live from the offer: a process without an offer, or an offer without a plan, keeps the
 * manual rounds. Round and interview `_id`s stay the same when the plan is edited, so an interview already
 * booked still matches its planned interview (`planInterviewId`).
 */

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

const OBJECT_ID = /^[0-9a-f]{24}$/i

/** The id of a value sent as an id or as a populated document, `null` when it is not an ObjectId */
const idOf = (value: unknown): string | null => {
  const id = isRecord(value) ? value._id : value
  const text = id === null || id === undefined ? '' : String(id)
  return OBJECT_ID.test(text) ? text : null
}

export const INTERVIEW_PLAN_EVALUATION_TYPES = ['form', 'minimal', 'coEvaluator', 'none'] as const
export type TInterviewPlanEvaluationType = (typeof INTERVIEW_PLAN_EVALUATION_TYPES)[number]

/** `preview`: the recruiter reviews and edits the email before it goes, `auto`: it goes on the decision */
export const INTERVIEW_PLAN_MAIL_MODES = ['preview', 'auto'] as const
export type TInterviewPlanMailMode = (typeof INTERVIEW_PLAN_MAIL_MODES)[number]

export const INTERVIEW_PLAN_LIMITS = {
  rounds: 10,
  interviewers: 20,
  interviews: 5,
  label: 80,
}

export type TInterviewPlanInterviewer = {
  resourceId: string
  /** `formId` only for the `form` type */
  evaluation: { type: TInterviewPlanEvaluationType, formId: string | null }
}

export type TInterviewPlanInterview = {
  _id?: string
  /** Interviewers who must all be on the slot (a joint interview); empty for any interviewer of the round */
  requiredInterviewerIds: string[]
}

export type TInterviewPlanRound = {
  _id?: string
  label: string
  interviewers: TInterviewPlanInterviewer[]
  /** One entry per interview of the round */
  interviews: TInterviewPlanInterview[]
  /** The candidate books the slots of the round from their space */
  candidateBooking: boolean
  mail: { templateId: string | null, mode: TInterviewPlanMailMode }
}

export type TInterviewPlan = TInterviewPlanRound[]

const uniqueIds = (values: unknown): string[] =>
  Array.isArray(values) ? [...new Set(values.map(idOf).filter((id): id is string => !!id))] : []

const sanitizeInterviewer = (value: unknown): TInterviewPlanInterviewer | null => {
  if (!isRecord(value))
    return null
  const resourceId = idOf(value.resourceId)
  if (!resourceId)
    return null
  const evaluation = isRecord(value.evaluation) ? value.evaluation : {}
  const type = INTERVIEW_PLAN_EVALUATION_TYPES.includes(evaluation.type as TInterviewPlanEvaluationType)
    ? evaluation.type as TInterviewPlanEvaluationType
    : 'minimal'
  const formId = type === 'form' ? idOf(evaluation.formId) : null
  // A form without its form is the minimal evaluation
  return { resourceId, evaluation: { type: type === 'form' && !formId ? 'minimal' : type, formId } }
}

const sanitizeRound = (value: unknown): TInterviewPlanRound => {
  const round = isRecord(value) ? value : {}
  const interviewers: TInterviewPlanInterviewer[] = []
  for (const item of Array.isArray(round.interviewers) ? round.interviewers : []) {
    const interviewer = sanitizeInterviewer(item)
    if (interviewer && !interviewers.some(current => current.resourceId === interviewer.resourceId))
      interviewers.push(interviewer)
  }
  interviewers.splice(INTERVIEW_PLAN_LIMITS.interviewers)
  const interviewerIds = new Set(interviewers.map(interviewer => interviewer.resourceId))

  const interviews = (Array.isArray(round.interviews) ? round.interviews : [])
    .slice(0, INTERVIEW_PLAN_LIMITS.interviews)
    .map((item): TInterviewPlanInterview => {
      const interview = isRecord(item) ? item : {}
      const interviewId = idOf(interview._id)
      return {
        ...(interviewId ? { _id: interviewId } : {}),
        requiredInterviewerIds: uniqueIds(interview.requiredInterviewerIds).filter(id => interviewerIds.has(id)),
      }
    })

  const roundId = idOf(round._id)
  const mail = isRecord(round.mail) ? round.mail : {}
  return {
    ...(roundId ? { _id: roundId } : {}),
    label: typeof round.label === 'string' ? round.label.trim().slice(0, INTERVIEW_PLAN_LIMITS.label) : '',
    interviewers,
    interviews,
    candidateBooking: round.candidateBooking !== false,
    mail: { templateId: idOf(mail.templateId), mode: mail.mode === 'auto' ? 'auto' : 'preview' },
  }
}

/** Keeps the known fields of each round, ids only, cut to the limits; anything else is dropped */
export const sanitizeInterviewPlan = (value: unknown): TInterviewPlan =>
  Array.isArray(value) ? value.slice(0, INTERVIEW_PLAN_LIMITS.rounds).map(sanitizeRound) : []

/**
 * The plan of an offer copied onto another one (import, apply to other offers): the same rounds, without the ids
 * of its rounds and interviews, which the processes of the first offer point to (`planInterviewId`)
 */
export const copyInterviewPlan = (value: unknown): TInterviewPlan =>
  sanitizeInterviewPlan(value).map(({ _id: _roundId, ...round }) => ({
    ...round,
    interviews: round.interviews.map(({ _id: _interviewId, ...interview }) => interview),
  }))

/** Planned round `roundIndex` of the offer of a process, `null` without a plan (or when the offer is deleted) */
export const getPlanRound = (
  offer: { isDeleted?: boolean | null, interviewPlan?: TInterviewPlanRound[] | null } | null | undefined,
  roundIndex: number,
): TInterviewPlanRound | null => {
  if (!offer || offer.isDeleted)
    return null
  return offer.interviewPlan?.[roundIndex] ?? null
}

/**
 * The planned round of a process with the interviews a recruiter let the candidate book in it (`rounds[].bookingPlan`
 * of the process, « Laisser le candidat choisir »), without a plan on the offer or on top of it: the interviews of
 * both, the interviewers of both (the plan's evaluation first). A plan round the recruiters book themselves
 * (candidateBooking false) gives the round its label only: its interviewers would widen the choice of the
 * interviews added for "any interviewer" of the request. `planRound` alone without a request.
 */
export const withBookingPlan = (planRound: TInterviewPlanRound | null, bookingPlan: unknown): TInterviewPlanRound | null => {
  const request = bookingPlan ? sanitizeRound({ ...(isRecord(bookingPlan) ? bookingPlan : {}), candidateBooking: true }) : null
  if (!request?.interviews.length)
    return planRound
  if (!planRound?.candidateBooking)
    return { ...request, label: planRound?.label || '', candidateBooking: true }
  return {
    ...planRound,
    interviewers: [
      ...planRound.interviewers,
      ...request.interviewers.filter(interviewer => !planRound.interviewers.some(current => current.resourceId === interviewer.resourceId)),
    ],
    interviews: [...planRound.interviews, ...request.interviews],
  }
}

/** withBookingPlan for a process as the front holds it: its offer populated (`recommendedBy.offer`) and its rounds */
export const getProcessPlanRound = (
  process: {
    recommendedBy?: { offer?: { isDeleted?: boolean | null, interviewPlan?: TInterviewPlanRound[] | null } | null } | null
    rounds?: ({ bookingPlan?: unknown } | null | undefined)[] | null
  } | null | undefined,
  roundIndex: number,
): TInterviewPlanRound | null => withBookingPlan(getPlanRound(process?.recommendedBy?.offer, roundIndex), process?.rounds?.[roundIndex]?.bookingPlan)

export type TRecruiterEvaluation = {
  evaluationForm: string | null
  basicEvaluation: boolean
  isCoEvaluator: boolean
  noEvaluation: boolean
}

/**
 * Evaluation fields of a recruiter of an interview (`rounds.interviews.recruiters`), as the plan gives them
 * to this recruiter. `null` when the recruiter is not an interviewer of the planned round: the caller keeps
 * its default (the evaluation form of the process).
 */
export const recruiterEvaluationFromPlan = (
  planRound: TInterviewPlanRound | null | undefined,
  recruiterId: unknown,
): TRecruiterEvaluation | null => {
  const id = idOf(recruiterId)
  const interviewer = id ? planRound?.interviewers.find(current => current.resourceId === id) : null
  if (!interviewer)
    return null
  const { type, formId } = interviewer.evaluation
  return {
    evaluationForm: type === 'form' ? formId : null,
    basicEvaluation: type === 'minimal',
    isCoEvaluator: type === 'coEvaluator',
    noEvaluation: type === 'none',
  }
}

export const CANDIDATE_BOOKING = {
  /** The recruiter is warned when a planned interview has no slot within these days */
  alertHorizonDays: 14,
  /** Furthest slot offered to the candidate, in days */
  bookingHorizonDays: 56,
  /** Nearest slot offered to the candidate, in hours */
  minNoticeHours: 24,
}

const idText = (value: unknown) => {
  const id = isRecord(value) ? value._id : value
  return id === null || id === undefined ? '' : String(id)
}

type TDate = Date | string

type TBookingRound = {
  goToNext?: TDate | null
  noGo?: TDate | null
  proposalDate?: TDate | null
  candidateCancelled?: TDate | null
}

/**
 * The round the candidate books in: the first round not passed yet (goToNext). Round 1 opens with « Rencontrer »
 * (screeningStatus 3) or once it exists, a later round once the round before goes to the next one. null when the
 * process is decided (stopped, offer made) or the candidate withdrew.
 */
export const getCandidateBookingRoundIndex = (
  process: { screeningStatus?: number | null, rounds?: TBookingRound[] | null } | null | undefined,
): number | null => {
  const rounds = process?.rounds || []
  if (rounds.some(round => round?.noGo || round?.proposalDate || round?.candidateCancelled))
    return null
  const openIndex = rounds.findIndex(round => !round?.goToNext)
  const roundIndex = openIndex === -1 ? rounds.length : openIndex
  if (roundIndex === 0 && !rounds[0] && process?.screeningStatus !== 3)
    return null
  return roundIndex
}

export type TBookedInterview = {
  planInterviewId?: unknown
  recruiters?: { recruiterId?: unknown }[] | null
}

const recruiterIdsOf = (interview: TBookedInterview) =>
  (interview.recruiters || []).map(recruiter => idText(recruiter.recruiterId)).filter(Boolean)

/** Whether interviewers `attendeeIds` hold a planned interview: all its required interviewers, or one of the round */
const fitsPlanInterview = (planRound: TInterviewPlanRound, planInterview: TInterviewPlanInterview, attendeeIds: string[]) =>
  planInterview.requiredInterviewerIds.length
    ? planInterview.requiredInterviewerIds.every(id => attendeeIds.includes(id))
    : planRound.interviewers.some(interviewer => attendeeIds.includes(interviewer.resourceId))

/**
 * Each planned interview of a round with the interview that holds it, null while the candidate has to book it.
 * An interview holds the planned interview it was booked for (`planInterviewId`); one a recruiter created by hand
 * holds a planned interview its recruiters fit, the ones with required interviewers first. So does one booked for
 * a planned interview the round no longer has: a plan copied onto the offer (import, apply) has new ids.
 */
export const matchPlanInterviews = <T extends TBookedInterview>(planRound: TInterviewPlanRound, interviews: T[]) => {
  const plannedIds = new Set(planRound.interviews.map(planInterview => planInterview._id).filter(Boolean))
  const remaining = [...interviews]
  const matches = planRound.interviews.map((planInterview) => {
    const index = planInterview._id ? remaining.findIndex(interview => idText(interview.planInterviewId) === planInterview._id) : -1
    return { planInterview, interview: index === -1 ? null : remaining.splice(index, 1)[0] }
  })
  const requiredFirst = [...matches].sort((a, b) => b.planInterview.requiredInterviewerIds.length - a.planInterview.requiredInterviewerIds.length)
  for (const match of requiredFirst) {
    if (match.interview)
      continue
    const index = remaining.findIndex(interview => !plannedIds.has(idText(interview.planInterviewId)) && fitsPlanInterview(planRound, match.planInterview, recruiterIdsOf(interview)))
    if (index !== -1)
      match.interview = remaining.splice(index, 1)[0]
  }
  return matches
}

/** Recruiters the candidate already meets in the round, but in `except` (the interview being booked) */
export const metInterviewerIds = (matches: { planInterview: TInterviewPlanInterview, interview: TBookedInterview | null }[], except?: string) =>
  matches.filter(match => match.interview && match.planInterview._id !== except).flatMap(match => recruiterIdsOf(match.interview as TBookedInterview))

export type TBookableSlot = {
  isMultiple?: boolean | null
  method?: string | null
  mode?: string | null
  videoConference?: { joinUrl?: string | null } | null
  events?: { start: TDate, end: TDate, attendees?: { attendeeId?: unknown }[] }[]
}

/**
 * Whether the candidate can book `slot` for a planned interview: a free slot (not a group interview, not
 * cancelled) between the minimal notice and `horizonDays`, a Teams slot with its link, holding the required
 * interviewers (or, without any, an interviewer of the round they do not meet yet in this round), and outside
 * their other interviews (`busyPeriods`).
 */
export const isSlotEligible = ({
  slot,
  planRound,
  planInterview,
  metInterviewers = [],
  busyPeriods = [],
  now = new Date(),
  horizonDays = CANDIDATE_BOOKING.bookingHorizonDays,
}: {
  slot: TBookableSlot
  planRound: TInterviewPlanRound
  planInterview: TInterviewPlanInterview
  metInterviewers?: string[]
  busyPeriods?: { start: TDate, end: TDate }[]
  now?: Date
  horizonDays?: number
}) => {
  const event = slot.events?.[0]
  if (!event || slot.isMultiple || slot.method === 'CANCEL')
    return false
  const start = new Date(event.start).getTime()
  const end = new Date(event.end).getTime()
  if (start < now.getTime() + CANDIDATE_BOOKING.minNoticeHours * 3_600_000 || start > now.getTime() + horizonDays * 86_400_000)
    return false
  if (slot.mode === 'teams' && !slot.videoConference?.joinUrl)
    return false
  const attendeeIds = (event.attendees || []).map(attendee => idText(attendee.attendeeId)).filter(Boolean)
  const met = new Set(metInterviewers)
  const fits = planInterview.requiredInterviewerIds.length
    ? fitsPlanInterview(planRound, planInterview, attendeeIds)
    : planRound.interviewers.some(interviewer => attendeeIds.includes(interviewer.resourceId) && !met.has(interviewer.resourceId))
  if (!fits)
    return false
  return !busyPeriods.some(period => new Date(period.start).getTime() < end && new Date(period.end).getTime() > start)
}
